import React from 'react';
import { motion } from 'motion/react';
import { CheckCircle2, Heart, Brain, Zap, user } from 'lucide-react';
import { Card } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';

const springConfig = {
  type: "spring",
  damping: 80,
  stiffness: 400,
  mass: 1
};

const About = () => {
  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-orange-50 via-yellow-50 to-green-50">
        <div className="container mx-auto max-w-7xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              About <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Goodikids</span>
            </h1>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Goodikids is a pioneering learning app designed for children and teens aged 5–18 years to build values, behaviour, confidence, emotional intelligence, life skills, discipline, and leadership through fun and interactive technology. Our mission is simple — help every child grow not just academically, but as a strong human being.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="prose prose-lg max-w-none"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6 font-sans">Our Story</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Goodikids began in 2023, when a group of parents, educators, psychologists, and technologists noticed a huge gap: children loved digital devices, but most apps focused only on academics or entertainment — not on character or real-life learning.
            </p>
            <blockquote className="border-l-4 border-orange-500 pl-6 italic text-gray-700 my-8">
              <p className="font-semibold text-xl mb-2">We asked a powerful question:</p>
              "What if learning life skills, values, and behaviour could be as engaging as playing a game?"
            </blockquote>
            <p className="text-gray-600 leading-relaxed">
              That question became the spark that created Goodikids. Today, Goodikids is helping families build confident, kind, disciplined, emotionally strong, and future-ready children — through playful learning at home.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How Goodikids Was Created */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="mb-12 text-center"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How Goodikids Was Created</h2>
            <p className="text-xl text-gray-700">Designed with heart. Built on research. Tested with real children.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
            >
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Goodikids was created by a cross-disciplinary team of child psychologists, value-education experts, behaviour specialists, storytellers, and UX designers, with a single vision — to make character development joyful and effective.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
            >
              <Card className="p-8 bg-white border-2 border-blue-100 shadow-xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Development Principles</h3>
                <ul className="space-y-4">
                  {[
                    "Research-backed learning — based on child psychology and SEL.",
                    "Warm and realistic content — reflecting real-life situations.",
                    "Field-tested — with children, parents, teachers, and counsellors.",
                    "Safe by design — no ads, no chats, no harmful links.",
                    "Continuously evolving — updated based on real feedback."
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Makes Goodikids Different */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">What Makes Goodikids Different</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Unlike traditional learning apps, Goodikids focuses on the person inside the child — not just marks or academics.
              We help children and teens learn:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              "Respect, kindness, honesty, discipline, empathy",
              "Self-control, time management, focus, responsibility",
              "Confidence, public speaking, manners, teamwork",
              "Emotional intelligence, stress management, communication",
              "Safety awareness & decision-making",
              "Leadership and life skills for real-world success"
            ].map((skill, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: i * 0.1 }}
              >
                <Card className="p-6 h-full bg-orange-50 border border-orange-100 hover:shadow-lg transition-all flex items-center">
                  <div className="w-2 h-2 rounded-full bg-orange-500 mr-4"></div>
                  <p className="text-gray-800 font-medium">{skill}</p>
                </Card>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-lg text-gray-600 italic">
            Learning isn’t passive — it’s interactive, gamified, emotional, and meaningful, so children enjoy it and parents see real change.
          </p>
        </div>
      </section>

      {/* Vision & Philosophy */}
      <section className="py-20 px-6 bg-gray-900 text-white">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
            >
              <h2 className="text-3xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-400">Our Vision</h2>
              <p className="text-lg text-gray-300 leading-relaxed">
                To create a world where every child grows with strong character, emotional balance, and the confidence to achieve their dreams — at home, in school, and in life.
              </p>
            </motion.div>

            {/* Philosophy */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="bg-gray-800 p-8 rounded-2xl border border-gray-700"
            >
              <h2 className="text-3xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Our Philosophy</h2>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-3">
                  <Heart className="w-6 h-6 text-red-400" />
                  <span className="text-xl">Heart to feel</span>
                </li>
                <li className="flex items-center gap-3">
                  <Brain className="w-6 h-6 text-purple-400" />
                  <span className="text-xl">Mind to think</span>
                </li>
                <li className="flex items-center gap-3">
                  <Zap className="w-6 h-6 text-yellow-400" />
                  <span className="text-xl">Discipline to act</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full border-2 border-green-400 flex items-center justify-center text-xs font-bold text-green-400">C</div>
                  <span className="text-xl">Character to become</span>
                </li>
              </ul>
              <p className="text-gray-400 italic">
                Goodikids is more than an app — it is a movement to raise kind, confident, and responsible human beings.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;